import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Card, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { vendorApi, BuSettingsError } from '@/utils/vendorApi';
import { businessUnitLabel } from '@/store/authStore';
import { BusinessUnitSetting } from '@/types';

const { Title, Paragraph, Text } = Typography;

function formatTimestamp(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm') : value;
}

const BusinessUnitSettingsPage: React.FC = () => {
  const { message } = App.useApp();
  const [settings, setSettings] = useState<BusinessUnitSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingBu, setSavingBu] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await vendorApi.listBuSettings();
      setSettings(rows);
    } catch (error: unknown) {
      const text = error instanceof BuSettingsError
        ? error.message
        : 'Failed to load business unit settings';
      setLoadError(text);
      setSettings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const onToggle = async (row: BusinessUnitSetting, adminFallbackApprover: boolean) => {
    setSavingBu(row.bu);
    try {
      const updated = await vendorApi.updateBuSetting(row.bu, adminFallbackApprover);
      setSettings((current) => current.map((item) => (item.bu === row.bu ? updated : item)));
      message.success(`Updated ${businessUnitLabel(row.bu)}`);
    } catch (error: unknown) {
      const text = error instanceof BuSettingsError
        ? error.message
        : 'Failed to update business unit settings';
      message.error(text);
    } finally {
      setSavingBu(null);
    }
  };

  const columns: ColumnsType<BusinessUnitSetting> = [
    {
      title: 'Business unit',
      dataIndex: 'bu',
      key: 'bu',
      render: (bu: string) => businessUnitLabel(bu),
    },
    {
      title: 'Admin can approve configuration changes when fewer than two Developers',
      dataIndex: 'adminFallbackApprover',
      key: 'adminFallbackApprover',
      render: (enabled: boolean, row) => (
        <Switch
          checked={enabled}
          disabled={savingBu === row.bu}
          loading={savingBu === row.bu}
          onChange={(checked) => void onToggle(row, checked)}
          aria-label={`Admin can approve configuration changes for ${businessUnitLabel(row.bu)}`}
        />
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value: string | null | undefined) => formatTimestamp(value),
    },
    {
      title: 'Updated by',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      render: (value: string | null | undefined) => value || '—',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginBottom: 0 }}>Business unit settings</Title>
      <Paragraph type="secondary" style={{ marginTop: 8, maxWidth: 720 }}>
        Controls whether a global Admin can approve a Developer&apos;s configuration change for this business unit. Off means only a second Developer can approve.
      </Paragraph>
      {loadError && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={loadError} />
      )}
      <Card>
        <Table<BusinessUnitSetting>
          rowKey="bu"
          columns={columns}
          dataSource={settings}
          loading={loading}
          pagination={false}
          locale={{ emptyText: loadError ? 'Settings could not be loaded' : 'No business units are configured' }}
        />
        {!loading && !loadError && settings.length > 0 && (
          <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            Changes apply to the next approval check for that business unit.
          </Text>
        )}
      </Card>
    </div>
  );
};

export default BusinessUnitSettingsPage;
